import { useGetAvailabilityByDeploymentQuery } from '../store/api/deploymentsApi';
import dayjs from 'dayjs';
import { dateTimeFormat } from '../lib/time';
import { roundToBookingIncrement } from '../helpers/booking';
import { UseAvailableDeploymentsParams } from '../types/deploymentTypes';
import {
    startTimeCannotBeExtended,
    startTimeCannotBeExtendedDueToUnavailability,
    endTimeCannotBeExtended,
    endTimeCannotBeExtendedDueToUnavailability,
    endTimeCanBeExtendedIndefinitely,
    startTimeCanBeExtendedTo,
    endTimeCanBeExtendedTo,
} from '../helpers/text';

const useAvailableDeployments = ({
    deploymentID,
    bookingDeploymentDetails,
    isCancelledBooking,
    isUpdateLoading,
}: UseAvailableDeploymentsParams) => {
    const {
        data: availabilityData,
        error: availabilityDataError,
        isLoading,
        isFetching,
    } = useGetAvailabilityByDeploymentQuery(deploymentID);

    let allowedStartTime;
    let allowedEndTime;

    if (!isUpdateLoading && availabilityData && !isFetching && !isLoading) {
        const currentTime = dayjs().tz();
        const currentBookingStartTime = dayjs(bookingDeploymentDetails.started_at).tz().format(dateTimeFormat);
        const currentBookingEndTime = dayjs(bookingDeploymentDetails.ended_at).tz().format(dateTimeFormat);

        // Logic for allowed start time.
        if (dayjs(bookingDeploymentDetails.started_at).tz() < currentTime || isCancelledBooking) {
            allowedStartTime = {
                message: startTimeCannotBeExtended,
                className: 'allowed-time-error-red',
            };
        } else {
            const availableTimeSlotBefore = availabilityData.filter(availability =>
                dayjs(availability.available_end).tz().format(dateTimeFormat) === currentBookingStartTime
            );

            if (availableTimeSlotBefore.length === 0) {
                allowedStartTime = {
                    message: startTimeCannotBeExtendedDueToUnavailability,
                    className: 'allowed-time-error-red',
                };
            } else {
                const availableStartTime = currentTime <= dayjs(availableTimeSlotBefore[0].available_start).tz()
                    ? dayjs(availableTimeSlotBefore[0].available_start).tz()
                    : currentTime;
                const availableStartTimeRounded = roundToBookingIncrement(availableStartTime, "up").format(dateTimeFormat);

                if (availableStartTimeRounded === currentBookingStartTime) {
                    allowedStartTime = {
                        message: startTimeCannotBeExtendedDueToUnavailability,
                        className: 'allowed-time-error-red',
                    };
                } else {
                    allowedStartTime = {
                        message: startTimeCanBeExtendedTo(availableStartTimeRounded),
                        className: 'allowed-time',
                    };
                }
            }
        }

        // Logic for allowed end time.
        if (dayjs(bookingDeploymentDetails.ended_at).tz() < currentTime || isCancelledBooking) {
            allowedEndTime = {
                message: endTimeCannotBeExtended,
                className: 'allowed-time-error-red',
            };
        } else {
            const availableTimeSlotAfter = availabilityData.filter(availability =>
                dayjs(availability.available_start).tz().format(dateTimeFormat) === currentBookingEndTime
            );

            if (availableTimeSlotAfter.length === 0) {
                allowedEndTime = {
                    message: endTimeCannotBeExtendedDueToUnavailability,
                    className: 'allowed-time-error-red',
                };
            } else if (availableTimeSlotAfter[0].available_end === null) {
                allowedEndTime = {
                    message: endTimeCanBeExtendedIndefinitely,
                    className: 'allowed-time',
                };
            } else {
                const availableEndTimeRounded = roundToBookingIncrement(dayjs(availableTimeSlotAfter[0].available_end).tz(), "down").format(dateTimeFormat);
                if (availableEndTimeRounded === currentBookingEndTime) {
                    allowedEndTime = {
                        message: endTimeCannotBeExtendedDueToUnavailability,
                        className: 'allowed-time-error-red',
                    };
                } else {
                    allowedEndTime = {
                        message: endTimeCanBeExtendedTo(availableEndTimeRounded),
                        className: 'allowed-time',
                    };
                }
            }
        }
    }

    return { allowedStartTime, allowedEndTime, availabilityDataError, isLoading };
};

export { useAvailableDeployments };
